import { isTaskType, type MapStep } from './model.js';

export interface StepConfirmation {
  /** Null for starts, ends and decisions: they need no citation. */
  confirmation: 'confirmed' | 'unconfirmed' | null;
  /**
   * The step cited evidence that is no longer confirmed (withdrawn or
   * superseded) and nothing confirms it now, so the consultant should link
   * other evidence, ask a follow-up or remove the step (Decision 54.20).
   */
  needs_attention: boolean;
}

/**
 * Whether a step is confirmed (rule 21, ADR-017 §1). Computed on every read,
 * never stored, so it follows the evidence: a task-type step is confirmed when
 * it cites evidence that is confirmed now, or the client's validation of a
 * version; otherwise it is unconfirmed. A step added in chat or by hand cites
 * nothing until evidence is linked, so it starts unconfirmed.
 */
export function stepConfirmation(step: Pick<MapStep, 'type' | 'citations'>): StepConfirmation {
  if (!isTaskType(step.type)) return { confirmation: null, needs_attention: false };
  let citesEvidence = false;
  for (const c of step.citations) {
    if (c.kind === 'client_validation')
      return { confirmation: 'confirmed', needs_attention: false };
    // Vendor knowledge says what a product does, not what the client does
    // today: it never confirms a step (a future step is cited or an assumption).
    if (c.kind !== 'discovery_evidence') continue;
    citesEvidence = true;
    if (c.evidence_status === 'confirmed')
      return { confirmation: 'confirmed', needs_attention: false };
  }
  return { confirmation: 'unconfirmed', needs_attention: citesEvidence };
}
